/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Renders a run result as a table.
 *
 * Deliberately not examples/Tables/DataTable: that component owns saved
 * filters, a filter drawer and localStorage state all keyed to a fixed
 * `objectType`, none of which applies to a report whose shape is decided at
 * run time. It expects columns known at build time; this gets them from the
 * response.
 *
 * Sorting is client-side and applies only to the rows already fetched. That is
 * stated in the UI rather than hidden, because a truncated result sorted
 * client-side is not the same as "the top 20 by value" and quietly implying
 * otherwise is how a report lies.
 */

import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Chip, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TableSortLabel, Typography,
} from '@mui/material';

import colors from 'styles/tokens/colors';
import { displayValue, formatValue } from './chartPalette';

const NUMERIC = new Set(['number', 'currency']);

/** One shared empty array, so an absent result keeps a stable identity. */
const EMPTY = [];

export default function ReportTable({
  result, currency, maxHeight, dense, onDrillDown, showRowCount,
}) {
  // Names for reference columns, resolved by the engine.
  const labels = result?.labels;
  const [sort, setSort] = useState({ key: null, direction: 'asc' });

  // Read straight off the result rather than defaulting to a fresh []: a new
  // array literal on every render is a new dependency identity, so the memo
  // below would recompute the sort on every render of a table that may hold
  // thousands of rows.
  const columns = result?.columns;
  const rows = result?.rows;

  const sorted = useMemo(() => {
    if (!rows) return EMPTY;
    if (!sort.key) return rows;
    const column = columns.find((c) => c.key === sort.key);
    const numeric = NUMERIC.has(column?.type);
    const factor = sort.direction === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const left = a[sort.key];
      const right = b[sort.key];
      // Nulls sort last in both directions: a blank is not "smallest", it is
      // absent, and burying it at the top hides the rows that matter.
      if (left === null || left === undefined) return 1;
      if (right === null || right === undefined) return -1;
      if (numeric) return (Number(left) - Number(right)) * factor;
      return String(left).localeCompare(String(right)) * factor;
    });
  }, [rows, columns, sort]);

  const toggleSort = (key) =>
    setSort((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));

  if (!columns?.length) {
    return (
      <Box sx={{ p: 5, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Choose an object and at least one column to see results.
        </Typography>
      </Box>
    );
  }

  const totals = result?.totals ?? {};
  const hasTotals = Object.keys(totals).length > 0;

  // Only a grouped row has anything to drill into — a tabular row already IS
  // the records. Offering it on both would make half the clicks do nothing.
  const drillable = Boolean(onDrillDown) && Boolean(result?.is_grouped);

  return (
    <Box>
      <TableContainer
        sx={{
          maxHeight: maxHeight || 'none',
          borderRadius: '12px',
          // Vendor theme (assets/theme/components/table/tableContainer.js) sets
          // MuiTableContainer overflow:hidden. With a maxHeight that clips the
          // body and never paints a scrollbar, so a drill-down that says
          // "scroll for all" cannot be scrolled (PSA-304). Restore scrolling
          // only when this table is height-capped; uncapped tables keep the
          // theme's radius clip.
          ...(maxHeight ? { '&&': { overflowX: 'auto', overflowY: 'auto' } } : {}),
        }}
      >
        <Table stickyHeader size={dense ? 'small' : 'medium'}>
          {/* The app's vendor theme (assets/theme/components/table/tableHead.js)
            globally sets MuiTableHead to display:block with padding and a
            border radius, for the card-style tables Material Dashboard ships.
            That takes the header row out of the table's column-width
            calculation, so every th sizes to its own content while the body
            cells size to the table — the header ends up a narrow pill above
            full-width rows.

            Restored locally rather than by changing the global, which ~160
            existing pages render against. */}
        <TableHead sx={{ display: 'table-header-group', p: 0, borderRadius: 0 }}>
            <TableRow>
              {(columns ?? EMPTY).map((column) => (
                <TableCell
                  key={column.key}
                  align={NUMERIC.has(column.type) ? 'right' : 'left'}
                  sx={{
                    fontWeight: 700, whiteSpace: 'nowrap',
                    backgroundColor: colors.background.offWhite,
                    color: colors.text.dark,
                  }}
                >
                  <TableSortLabel
                    active={sort.key === column.key}
                    direction={sort.key === column.key ? sort.direction : 'asc'}
                    onClick={() => toggleSort(column.key)}
                  >
                    {column.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {!sorted.length ? (
              <TableRow>
                <TableCell colSpan={columns.length} sx={{ py: 6, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No records match this report&apos;s filters.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : sorted.map((row, index) => (
              // Report rows have no stable identity — a grouped row is not a
              // record — so the index is the only key available.
              // eslint-disable-next-line react/no-array-index-key
              <TableRow
                key={index}
                hover
                onClick={drillable ? () => onDrillDown(row) : undefined}
                sx={drillable ? { cursor: 'pointer' } : undefined}
              >
                {(columns ?? EMPTY).map((column) => (
                  <TableCell
                    key={column.key}
                    align={NUMERIC.has(column.type) ? 'right' : 'left'}
                    sx={{
                      whiteSpace: 'nowrap',
                      fontVariantNumeric: NUMERIC.has(column.type)
                        ? 'tabular-nums' : 'normal',
                    }}
                  >
                    {displayValue(row[column.key], column, currency, labels)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {(hasTotals || result?.truncated || drillable || showRowCount) && (
        <Box
          display="flex" alignItems="center" gap={2} flexWrap="wrap"
          sx={{ px: 2, py: 1.25, borderTop: '1px solid', borderColor: 'divider' }}
        >
          {showRowCount && typeof result?.row_count === 'number' && (
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              {result.row_count.toLocaleString()} record{result.row_count === 1 ? '' : 's'}
              {maxHeight ? ' (scroll for all)' : ''}
            </Typography>
          )}
          {hasTotals && columns.filter((c) => totals[c.key] !== undefined).map((column) => (
            <Typography key={column.key} variant="caption" color="text.secondary">
              <strong>{column.label}:</strong>{' '}
              {formatValue(totals[column.key], column, currency)}
            </Typography>
          ))}
          {drillable && (
            <Typography variant="caption" color="text.secondary">
              Click a row to see the records behind it.
            </Typography>
          )}
          {result?.truncated && (
            // Never silent. A report that returns the first N of many and says
            // nothing is a wrong answer, not a slow one.
            <Chip
              size="small"
              label={`Showing the first ${result.limit.toLocaleString()} rows — narrow the filters to see the rest`}
              sx={{
                backgroundColor: colors.status.warning.background,
                color: colors.status.warning.text,
                fontWeight: 600,
              }}
            />
          )}
        </Box>
      )}
    </Box>
  );
}

ReportTable.propTypes = {
  result: PropTypes.object,
  currency: PropTypes.string,
  maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  dense: PropTypes.bool,
  /** Called with the clicked row. Only wired up for grouped reports. */
  onDrillDown: PropTypes.func,
  /** Always show how many rows the engine returned (used by drill-down). */
  showRowCount: PropTypes.bool,
};
